import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useWebSocket } from "WebSocketContext";

import {
  ACTION_OPTIONS,
  DEFAULT_WM,
  applyPlayerAction,
  createInitialGameState,
  finalizeActions as finalizeActionsState,
  extendGame as extendGameState,
  endGame as endGameState,
  runSimulation as runSimulationState,
  updateWaterManagementChoice,
  recordWaterManagementVote,
  removeWaterManagementVote,
  adjustCurrentPhaseTimer,
  updatePhaseTimerSettings,
  hasPhaseTimerExpired,
  type ActionSelection,
  type GameState,
  type PlayerKey,
  type TimerSettings,
  type WaterManagement,
} from "pages/game/vow/vowGameLogic";

type VowPlayer = {
  id: number;
  playerName?: string;
  role?: PlayerKey | null;
  roleLocked?: boolean;
};

type VowGameContextValue = {
  sessionVersion: number;
  resetSession: () => void;
  playerId: number;
  hostId: number | null;
  setHostId: (id: number) => void;
  gameId: string;
  setGameId: (id: string) => void;
  isHost: boolean;
  players: VowPlayer[];
  connectedIds: number[];
  gameState: GameState | null;
  createOrJoinHostRoom: (gameCode: string) => void;
  startGame: () => void;
  assignSelfRole: (role: PlayerKey, playerName: string) => void;
  requestAction: (selection: ActionSelection, tiles: number[]) => void;
  finalizeActions: () => void;
  runSimulation: () => void;
  resetGame: () => void;
  changeWaterManagement: (value: WaterManagement) => void;
  adjustPhaseTimer: (deltaMs: number) => void;
  updateTimerSettings: (updates: Partial<TimerSettings>) => void;
  extendGame: () => void;
  endGame: () => void;
  selfRole: PlayerKey | null;
};

const VowGameContext = createContext<VowGameContextValue | undefined>(undefined);

const findActionOption = (selection: ActionSelection) =>
  ACTION_OPTIONS.find((option) => option.value === selection);

export const VowGameContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { socket, playerId } = useWebSocket();

  const [sessionVersion, setSessionVersion] = useState(0);

  const [gameId, setGameId] = useState("");
  const [hostId, setHostId] = useState<number | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [playersMap, setPlayersMap] = useState<Map<number, VowPlayer>>(new Map());

  const latestStateRef = useRef<GameState | null>(null);
  useEffect(() => {
    latestStateRef.current = gameState;
  }, [gameState]);

  const isHost = hostId !== null && playerId === hostId;

  const players = useMemo(() => Array.from(playersMap.values()), [playersMap]);

  const connectedIds = useMemo(() => {
    const ids: number[] = [];
    if (hostId && hostId > 0) {
      ids.push(hostId);
    }
    players.forEach((player) => {
      if (player.id > 0 && player.id !== hostId) {
        ids.push(player.id);
      }
    });
    return ids;
  }, [players, hostId]);

  const selfRole = playersMap.get(playerId)?.role ?? null;

  const broadcastState = useCallback((state: GameState, targetIds?: number[]) => {
    if (!socket) return;
    const ids = (targetIds ?? connectedIds).filter((id) => id > 0 && id !== playerId);
    if (ids.length === 0) return;
    socket.send(
      JSON.stringify({
        kind: "VowStateSync",
        toIds: ids,
        gameState: state,
      })
    );
  }, [socket, connectedIds, playerId]);

  const commitHostState = useCallback((transform: (prev: GameState) => GameState) => {
    if (!isHost) return;
    setGameState((prev) => {
      if (!prev) return prev;
      const updated = transform(prev);
      broadcastState(updated);
      return updated;
    });
  }, [isHost, broadcastState]);

  const createOrJoinHostRoom = useCallback((gameCode: string) => {
    if (!socket || playerId <= 0) return;
    setHostId(playerId);
    setGameId(gameCode);

    socket.send(JSON.stringify({
      kind: "CreateGame",
      hostId: playerId,
      gameInviteCode: gameCode,
    }));
  }, [socket, playerId]);

  const startGame = useCallback(() => {
    if (!isHost) return;

    let createdState: GameState | null = null;
    setGameState((prev) => {
      if (prev) return prev;
      createdState = createInitialGameState(DEFAULT_WM);
      return createdState;
    });

    if (createdState) {
      setSessionVersion((prev) => prev + 1);
      broadcastState(createdState);
    }
  }, [broadcastState, isHost]);

  const assignSelfRole = useCallback((role: PlayerKey, playerName: string) => {
    if (!socket || playerId <= 0) return;

    let allowed = true;
    let finalRole: PlayerKey = role;

    setPlayersMap((prev) => {
      const next = new Map(prev);
      const existing = next.get(playerId) ?? { id: playerId };
      const existingRole = existing.role ?? null;
      const isLocked = Boolean(existing.roleLocked);

      if (isLocked && existingRole && existingRole !== role) {
        allowed = false;
        return prev;
      }

      if (isLocked && existingRole) {
        finalRole = existingRole;
      }

      next.set(playerId, {
        ...existing,
        role: finalRole,
        playerName,
        roleLocked: true,
      });
      return next;
    });

    if (!allowed) {
      return;
    }

    const targetIds = connectedIds.length > 0
      ? connectedIds
      : hostId && hostId > 0
        ? [hostId]
        : [];

    if (targetIds.length > 0) {
      socket.send(JSON.stringify({
        kind: "UpdatePlayerInfo",
        playerId,
        toIds: targetIds,
        newData: {
          role: finalRole,
          playerName,
          roleLocked: true,
        },
      }));
    }
  }, [socket, playerId, connectedIds, hostId]);

  const requestAction = useCallback((selection: ActionSelection, tileIndices: number[]) => {
    if (tileIndices.length === 0) {
      return;
    }

    const option = findActionOption(selection);
    if (!option) return;

    const latestState = latestStateRef.current;
    if (!isHost && latestState && hasPhaseTimerExpired(latestState)) {
      return;
    }

    if (isHost) {
      commitHostState((prev) =>
        applyPlayerAction(prev, {
          action: option.actionKey,
          playerKey: option.playerKey,
          tileIndices,
        })
      );
      return;
    }

    if (!socket || !hostId) return;

    socket.send(JSON.stringify({
      kind: "VowActionRequest",
      toIds: [hostId],
      fromId: playerId,
      action: option.actionKey,
      playerKey: option.playerKey,
      tileIndices,
      gameId,
    }));
  }, [commitHostState, gameId, hostId, isHost, playerId, socket]);

  const finalizeActions = useCallback(() => {
    if (!selfRole && !isHost) return;

    if (isHost) {
      commitHostState((prev) => finalizeActionsState(prev));
      return;
    }

    if (!socket || !hostId) return;
    socket.send(JSON.stringify({
      kind: "VowFinalizeRequest",
      toIds: [hostId],
      fromId: playerId,
      gameId,
    }));
  }, [commitHostState, gameId, hostId, isHost, playerId, socket, selfRole]);

  const runSimulation = useCallback(() => {
    if (!selfRole && !isHost) return;

    if (isHost) {
      commitHostState((prev) => runSimulationState(prev));
      return;
    }

    if (!socket || !hostId) return;
    socket.send(JSON.stringify({
      kind: "VowRunSimulationRequest",
      toIds: [hostId],
      fromId: playerId,
      gameId,
    }));
  }, [commitHostState, gameId, hostId, isHost, playerId, socket, selfRole]);

  const extendGame = useCallback(() => {
    if (!isHost) return;
    commitHostState((prev) => extendGameState(prev));
  }, [commitHostState, isHost]);

  const endGame = useCallback(() => {
    if (!isHost) return;
    commitHostState((prev) => endGameState(prev));
  }, [commitHostState, isHost]);

  const resetGame = useCallback(() => {
    if (isHost) {
      const initialState = createInitialGameState(DEFAULT_WM);
      setGameState(initialState);
      setSessionVersion((prev) => prev + 1);
      broadcastState(initialState);
      return;
    }

    if (!socket || !hostId) return;
    socket.send(JSON.stringify({
      kind: "VowResetRequest",
      toIds: [hostId],
      fromId: playerId,
      gameId,
    }));
  }, [broadcastState, hostId, isHost, playerId, socket]);

  const changeWaterManagement = useCallback((value: WaterManagement) => {
    if (isHost) {
      commitHostState((prev) => updateWaterManagementChoice(prev, value));
      return;
    }

    const latestState = latestStateRef.current;
    if (!latestState || latestState.phase !== "simulation") {
      return;
    }
    if (hasPhaseTimerExpired(latestState)) {
      return;
    }

    if (!socket || !hostId) return;
    socket.send(JSON.stringify({
      kind: "VowWaterManagementVote",
      toIds: [hostId],
      fromId: playerId,
      gameId,
      value,
    }));
  }, [commitHostState, gameId, hostId, isHost, playerId, socket]);

  const adjustPhaseTimer = useCallback((deltaMs: number) => {
    if (!isHost || deltaMs === 0) return;
    commitHostState((prev) => adjustCurrentPhaseTimer(prev, deltaMs));
  }, [commitHostState, isHost]);

  const updateTimerSettings = useCallback((updates: Partial<TimerSettings>) => {
    if (!isHost) return;
    commitHostState((prev) => updatePhaseTimerSettings(prev, updates));
  }, [commitHostState, isHost]);

  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent<string>) => {
      if (!event.data) return;

      let message: any;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }

      if (message.kind === "GameCreated") {
        if (isHost) {
          setPlayersMap(new Map());
        }
      }

      if (message.kind === "JoinedGame") {
        const joinedId: number = message.playerId;
        setPlayersMap((prev) => {
          const next = new Map(prev);
          if (!next.has(joinedId)) {
            next.set(joinedId, { id: joinedId, role: null });
          }
          return next;
        });

        if (isHost && latestStateRef.current) {
          broadcastState(latestStateRef.current, [joinedId]);
        }
      }

      if (message.kind === "PlayerDisconnected") {
        const disconnectedId: number = message.playerId;
        setPlayersMap((prev) => {
          const next = new Map(prev);
          next.delete(disconnectedId);
          return next;
        });
        if (isHost) {
          commitHostState((prev) => removeWaterManagementVote(prev, disconnectedId));
        }
      }

      if (message.kind === "UpdatePlayerInfo") {
        setPlayersMap((prev) => {
          const next = new Map(prev);
          const existing = next.get(message.playerId) ?? { id: message.playerId };
          next.set(message.playerId, {
            ...existing,
            ...message.newData,
          });
          return next;
        });
      }

      if (message.kind === "UpdateAllPlayersInfo") {
        const incoming = new Map<number, VowPlayer>();
        if (Array.isArray(message.allPlayers)) {
          message.allPlayers.forEach((player: VowPlayer) => {
            incoming.set(player.id, player);
          });
        }
        setPlayersMap(incoming);
      }

      if (message.kind === "VowStateSync" && message.gameState) {
        setGameState(message.gameState);
      }

      if (message.kind === "VowActionRequest" && isHost) {
        commitHostState((prev) =>
          applyPlayerAction(prev, {
            action: message.action,
            playerKey: message.playerKey,
            tileIndices: Array.isArray(message.tileIndices) ? message.tileIndices : [],
          })
        );
      }

      if (message.kind === "VowFinalizeRequest" && isHost) {
        commitHostState((prev) => finalizeActionsState(prev));
      }

      if (message.kind === "VowRunSimulationRequest" && isHost) {
        commitHostState((prev) => runSimulationState(prev));
      }

      if (message.kind === "VowResetRequest" && isHost) {
        const initialState = createInitialGameState(DEFAULT_WM);
        setGameState(initialState);
        setSessionVersion((prev) => prev + 1);
        broadcastState(initialState);
      }

      if (message.kind === "VowWaterManagementVote" && isHost) {
        const voterId = typeof message.fromId === "number" ? message.fromId : 0;
        commitHostState((prev) => recordWaterManagementVote(prev, voterId, message.value));
      }
    };

    socket.addEventListener("message", handleMessage);
    return () => {
      socket.removeEventListener("message", handleMessage);
    };
  }, [socket, isHost, broadcastState, commitHostState]);

  const resetSession = useCallback(() => {
    setSessionVersion((prev) => prev + 1);
  }, []);
  const value: VowGameContextValue = {
    sessionVersion,
    resetSession,
    playerId,
    hostId,
    setHostId,
    gameId,
    setGameId,
    isHost,
    players,
    connectedIds,
    gameState,
    createOrJoinHostRoom,
    startGame,
    assignSelfRole,
    requestAction,
    finalizeActions,
    runSimulation,
    resetGame,
    changeWaterManagement,
    adjustPhaseTimer,
    updateTimerSettings,
    extendGame,
    endGame,
    selfRole,
  };

  return (
    <VowGameContext.Provider value={value}>
      {children}
    </VowGameContext.Provider>
  );
};

export const useVowGameContext = () => {
  const context = useContext(VowGameContext);
  if (!context) {
    throw new Error("useVowGameContext must be used inside VowGameContextProvider");
  }
  return context;
};
